import { DEFAULT_FACE_WIDTH } from "@/lib/typology";
import { infillOf } from "@/lib/infill";

export const OPENINGS = [
  { value: "fixed", label: "Fixed", code: "F" },
  { value: "casement_left", label: "Side hung · hinge L", code: "SH-L" },
  { value: "casement_right", label: "Side hung · hinge R", code: "SH-R" },
  { value: "awning", label: "Top hung", code: "TH" },
  { value: "hopper", label: "Bottom hung", code: "BH" },
  { value: "tilt_turn_left", label: "Tilt & turn · L", code: "TT-L" },
  { value: "tilt_turn_right", label: "Tilt & turn · R", code: "TT-R" },
  { value: "sliding_left", label: "Sliding ← L", code: "SL-L" },
  { value: "sliding_right", label: "Sliding → R", code: "SL-R" },
  { value: "sliding_up", label: "Vertical sliding ↑", code: "VS-U" },
  { value: "sliding_down", label: "Vertical sliding ↓", code: "VS-D" },
  { value: "pivot_vertical", label: "Pivot · vertical axis", code: "PV-V" },
  { value: "pivot_horizontal", label: "Pivot · horizontal axis", code: "PV-H" },
  { value: "louvre", label: "Louvre", code: "LV" },
  { value: "folding_left", label: "Bi-fold ← L", code: "BF-L" },
  { value: "folding_right", label: "Bi-fold → R", code: "BF-R" },
  { value: "door_left", label: "Door · hinge L", code: "DR-L" },
  { value: "door_right", label: "Door · hinge R", code: "DR-R" },
];
export const openingLabel = (v) => OPENINGS.find((o) => o.value === v)?.label || v;
export { PROFILE_PRESETS } from "@/lib/typology";

const cum = (arr) => arr.reduce((a, v) => [...a, a[a.length - 1] + v], [0]);

const pushRect = (P, x, y, w, h, layer, weight, fill) => P.push({ t: "rect", x, y, w, h, layer, weight, fill });
const pushLine = (P, x1, y1, x2, y2, layer, weight = "thin", dash = false) => P.push({ t: "line", x1, y1, x2, y2, layer, weight, dash });
const pushText = (P, x, y, text, h, anchor = "middle", rot = 0, layer = "TEXT") => P.push({ t: "text", x, y, text: String(text), h, anchor, rot, layer });

function symbol(P, op, x, y, w, h) {
  const L = (a, b, c, d) => pushLine(P, a, b, c, d, "SYMBOL", "thin", true);
  const cx = x + w / 2, cy = y + h / 2;
  const hingeLeft = () => { L(x + w, y, x, cy); L(x + w, y + h, x, cy); };
  const hingeRight = () => { L(x, y, x + w, cy); L(x, y + h, x + w, cy); };
  const top = () => { L(x, y + h, cx, y); L(x + w, y + h, cx, y); };
  const bottom = () => { L(x, y, cx, y + h); L(x + w, y, cx, y + h); };
  const arrow = (dir) => {
    const a = x + w * 0.22, b = x + w * 0.78, s = Math.min(w, h) * 0.07;
    pushLine(P, a, cy, b, cy, "SYMBOL", "medium");
    const tip = dir < 0 ? a : b;
    pushLine(P, tip, cy, tip - dir * s, cy - s, "SYMBOL", "medium");
    pushLine(P, tip, cy, tip - dir * s, cy + s, "SYMBOL", "medium");
  };
  const varrow = (dir) => {
    const a = y + h * 0.25, b = y + h * 0.75, s = Math.min(w, h) * 0.07, tip = dir < 0 ? a : b;
    pushLine(P, cx, a, cx, b, "SYMBOL", "medium");
    pushLine(P, cx, tip, cx - s, tip - dir * s, "SYMBOL", "medium");
    pushLine(P, cx, tip, cx + s, tip - dir * s, "SYMBOL", "medium");
  };
  const louvre = () => { for (let k = 1; k < 8; k++) { const yy = y + (h * k) / 8; pushLine(P, x + w * 0.06, yy, x + w * 0.94, yy - h * 0.03, "SYMBOL", "thin"); } };
  const handle = (hx) => pushRect(P, hx - 12, cy - 80, 24, 160, "HARDWARE", "thin", "frame");
  switch (op) {
    case "casement_left": hingeLeft(); break;
    case "casement_right": hingeRight(); break;
    case "awning": top(); break;
    case "hopper": bottom(); break;
    case "tilt_turn_left": hingeLeft(); bottom(); break;
    case "tilt_turn_right": hingeRight(); bottom(); break;
    case "sliding_left": arrow(-1); break;
    case "sliding_right": arrow(1); break;
    case "sliding_up": varrow(-1); break;
    case "sliding_down": varrow(1); break;
    case "pivot_vertical": L(x, y, cx, cy); L(x, y + h, cx, cy); L(x + w, y, cx, cy); L(x + w, y + h, cx, cy); pushLine(P, cx, y, cx, y + h, "SYMBOL", "thin"); break;
    case "pivot_horizontal": L(x, y, cx, cy); L(x + w, y, cx, cy); L(x, y + h, cx, cy); L(x + w, y + h, cx, cy); pushLine(P, x, cy, x + w, cy, "SYMBOL", "thin"); break;
    case "louvre": louvre(); break;
    case "folding_left": hingeLeft(); arrow(-1); break;
    case "folding_right": hingeRight(); arrow(1); break;
    case "door_left": hingeLeft(); handle(x + w - 60); break;
    case "door_right": hingeRight(); handle(x + 60); break;
    default: break;
  }
}

function dimH(P, xa, xb, yBase, yLine, th) {
  const ext = th * 0.5, tk = th * 0.45;
  pushLine(P, xa, yBase + th * 0.3, xa, yLine + ext, "DIM");
  pushLine(P, xb, yBase + th * 0.3, xb, yLine + ext, "DIM");
  pushLine(P, xa, yLine, xb, yLine, "DIM");
  pushLine(P, xa - tk, yLine + tk, xa + tk, yLine - tk, "DIM", "medium");
  pushLine(P, xb - tk, yLine + tk, xb + tk, yLine - tk, "DIM", "medium");
  pushText(P, (xa + xb) / 2, yLine - th * 0.35, Math.round(xb - xa), th, "middle", 0, "DIMTEXT");
}

function dimV(P, ya, yb, xBase, xLine, th) {
  const ext = th * 0.5, tk = th * 0.45;
  pushLine(P, xBase + th * 0.3, ya, xLine + ext, ya, "DIM");
  pushLine(P, xBase + th * 0.3, yb, xLine + ext, yb, "DIM");
  pushLine(P, xLine, ya, xLine, yb, "DIM");
  pushLine(P, xLine - tk, ya + tk, xLine + tk, ya - tk, "DIM", "medium");
  pushLine(P, xLine - tk, yb + tk, xLine + tk, yb - tk, "DIM", "medium");
  pushText(P, xLine - th * 0.35, (ya + yb) / 2, Math.round(yb - ya), th, "middle", -90, "DIMTEXT");
}

export const spanAt = (item, r, c) => (item.spans || []).find((s) => s.r === r && s.c === c) || { r, c, rs: 1, cs: 1 };
const covers = (s, r, c) => r >= s.r && r < s.r + s.rs && c >= s.c && c < s.c + s.cs;
export const anchorOf = (item, r, c) => {
  const s = (item.spans || []).find((x) => covers(x, r, c));
  return s ? { r: s.r, c: s.c } : { r, c };
};
export const visibleCells = (item) => item.rows.flatMap((_, r) => item.columns.map((__, c) => ({ r, c })))
  .filter(({ r, c }) => { const a = anchorOf(item, r, c); return a.r === r && a.c === c; })
  .map(({ r, c }) => spanAt(item, r, c));

export function mergeCell(item, r, c, dir) {
  const s = spanAt(item, r, c);
  const next = { r, c, rs: s.rs + (dir === "down" ? 1 : 0), cs: s.cs + (dir === "right" ? 1 : 0) };
  if (next.r + next.rs > item.rows.length || next.c + next.cs > item.columns.length) return item;
  const intersects = (x) => x.r < next.r + next.rs && next.r < x.r + x.rs && x.c < next.c + next.cs && next.c < x.c + x.cs;
  return compact({ ...item, spans: [...(item.spans || []).filter((x) => !intersects(x)), next] });
}
export const splitCell = (item, r, c) => ({ ...item, spans: (item.spans || []).filter((s) => !(s.r === r && s.c === c)) });

export const GLASS_JOINT = 12;
export const isCorner = (k) => !!k && +k.angle > 0 && +k.angle < 180;
const crosses = (P, x) => P.some((p) => p.x0 < x && p.x1 > x);
export function glassCornerOk(item, i) {
  const x = cum(item.columns)[i], P = panelsOf(item);
  return !crosses(P, x) && P.filter((p) => p.x1 === x || p.x0 === x).every((p) => p.op === "fixed");
}
const jointW = (item, i) => {
  const k = item.corners?.[i];
  return isCorner(k) && k.type === "glass" && glassCornerOk(item, i) ? GLASS_JOINT : (item.mullion_width ?? 50);
};
export function activeCorners(item) {
  const xs = cum(item.columns), P = panelsOf(item);
  return Object.entries(item.corners || {}).map(([i, k]) => ({ ...k, i: +i, x: xs[+i] }))
    .filter((k) => k.i > 0 && k.i < item.columns.length && isCorner(k) && !crosses(P, k.x))
    .map((k) => ({ ...k, angle: +k.angle, type: k.type === "glass" && glassCornerOk(item, k.i) ? "glass" : "post", dir: k.dir === "int" ? "int" : "ext" }))
    .sort((a, b) => a.x - b.x);
}

export function cellRect(item, r, c) {
  const { width: W, height: H, columns, rows } = item;
  const f = item.frame_width ?? 50, m = item.mullion_width ?? 50;
  const xs = cum(columns), ys = cum(rows);
  const { rs, cs } = spanAt(item, r, c);
  const ce = c + cs - 1, re = r + rs - 1;
  const x0 = c === 0 ? f : xs[c] + jointW(item, c) / 2, x1 = ce === columns.length - 1 ? W - f : xs[ce + 1] - jointW(item, ce + 1) / 2;
  const y0 = r === 0 ? f : ys[r] + m / 2, y1 = re === rows.length - 1 ? H - f : ys[re + 1] - m / 2;
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
}

function railingPrims(P, item, th, labels) {
  const { height: rh, bottom = 150 } = item.railing, H = item.height, f = item.frame_width ?? 50, R = 40;
  const last = item.rows.length - 1;
  visibleCells(item).filter(({ r, c, rs }) => r + rs - 1 === last && (item.cells[r][c] || "").startsWith("sliding")).forEach(({ r, c }) => {
    const cr = cellRect(item, r, c), x0 = cr.x + 10, w = cr.w - 20;
    const yTop = H - f - rh, yBot = H - f - bottom - R;
    if (yTop < cr.y) return;
    P.push({ t: "rect", x: x0, y: yTop, w, h: R, layer: "SASH", weight: "medium", fill: "frame", dxf: "RAILING" });
    P.push({ t: "rect", x: x0, y: yBot, w, h: R, layer: "SASH", weight: "medium", fill: "frame", dxf: "RAILING" });
    P.push({ t: "rect", x: x0 + 15, y: yTop + R, w: w - 30, h: yBot - yTop - R, layer: "GLASS", weight: "thin", fill: null, dxf: "RAILING" });
    for (let k = 1; k < 6; k++) pushLine(P, x0 + (w * k) / 6, yTop + R, x0 + (w * k) / 6, yBot, "GLASS", "thin", true);
    if (labels) pushText(P, x0 + w / 2, yTop - th * 0.4, `INTEGRATED RAILING ${rh}`, th * 0.55, "middle", 0, "GLASSNOTE");
  });
}

const cornerText = (k) => `${k.angle}° ${k.type === "glass" ? "GLASS-TO-GLASS CORNER" : "CORNER POST"}${k.dir === "int" ? " (INTERNAL)" : ""}`;

function foldPrims(P, item, th) {
  const { width: W, height: H } = item, f = item.frame_width ?? 50, m = item.mullion_width ?? 50;
  const fold = (x, lx, text) => {
    P.push({ t: "line", x1: x, y1: -th * 0.5, x2: x, y2: H + th * 0.5, layer: "FOLD", weight: "medium", dash: true, dxf: "CORNER_FOLD" });
    P.push({ t: "text", x: lx, y: f + th * 0.6, text, h: th * 0.6, anchor: "end", rot: -90, layer: "FOLDTEXT", bold: true, dxf: "CORNER_FOLD" });
  };
  activeCorners(item).forEach((k) => {
    if (k.type === "glass") P.push({ t: "rect", x: k.x - GLASS_JOINT / 2, y: f, w: GLASS_JOINT, h: H - 2 * f, layer: "FRAME", weight: "thin", fill: "#475569", dxf: "SILICONE_JOINT" });
    fold(k.x, k.x + (k.type === "glass" ? GLASS_JOINT : m) / 2 + th * 0.9, cornerText(k));
  });
  const cp = item._couple || {};
  const edge = (k) => (isCorner(k) ? `${k.angle}° CORNER${k.dir === "int" ? " (INT.)" : ""} · ${k.tag}` : `COUPLED · ${k.tag}`);
  if (cp.left) fold(0, f + th * 0.9, edge(cp.left));
  if (cp.right) fold(W, W - f - th * 0.2, edge(cp.right));
}

export function buildPrimitives(item, { dims = true, labels = true } = {}) {
  const P = [];
  const { width: W, height: H, columns, rows, cells } = item;
  const f = item.frame_width ?? 50, s = item.sash_width ?? 50;
  const E = Math.max(W, H, 600);
  const th = E * 0.026;
  const xs = cum(columns), ys = cum(rows);

  pushRect(P, 0, 0, W, H, "FRAME", "heavy", "frame");
  pushRect(P, f, f, W - 2 * f, H - 2 * f, "FRAME", "medium", null);

  visibleCells(item).forEach(({ r, c }) => {
    const cr = cellRect(item, r, c);
    const op = cells?.[r]?.[c] || "fixed";
    let g = cr;
    if (op !== "fixed" && op !== "louvre") {
      pushRect(P, cr.x, cr.y, cr.w, cr.h, "SASH", "medium", "frame");
      const ss = Math.min(s, cr.w / 4, cr.h / 4);
      g = { x: cr.x + ss, y: cr.y + ss, w: cr.w - 2 * ss, h: cr.h - 2 * ss };
    }
    const pg = item._eff_glass ? item._eff_glass[`${r}-${c}`] : item.panel_glass?.[`${r}-${c}`], inf = infillOf(pg || item.glass_spec);
    if (inf) P.push({ t: "rect", x: g.x, y: g.y, w: g.w, h: g.h, layer: "INFILL", weight: "thin", fill: `infill:${inf.key}`, dxf: `INFILL_${inf.key}` });
    else pushRect(P, g.x, g.y, g.w, g.h, "GLASS", "thin", "glass");
    if (op === "fixed" || op === "louvre") pushRect(P, cr.x, cr.y, cr.w, cr.h, "FRAME", "medium", null);
    const gl = Math.min(g.w, g.h) * 0.12;
    if (!inf) {
      pushLine(P, g.x + g.w * 0.62, g.y + gl, g.x + g.w * 0.62 + gl, g.y + gl * 0.2 + 0.0001, "GLASS", "thin");
      pushLine(P, g.x + g.w * 0.66, g.y + gl * 1.5, g.x + g.w * 0.66 + gl * 1.3, g.y + gl * 0.4, "GLASS", "thin");
    }
    symbol(P, op, g.x, g.y, g.w, g.h);
    if (labels && op === "fixed") pushText(P, g.x + g.w / 2, g.y + g.h / 2 + th * 0.35, "FIXED", th * 0.7, "middle", 0, "LABEL");
    if (labels && pg) {
      const maxCh = Math.max(4, Math.floor(g.w / (th * 0.55 * 0.62)));
      pushText(P, g.x + g.w / 2, g.y + g.h - th * 0.6, pg.length > maxCh ? `${pg.slice(0, maxCh - 1)}…` : pg, th * 0.55, "middle", 0, "GLASSNOTE");
    }
  });

  if (item.railing?.height) railingPrims(P, item, th, labels);
  foldPrims(P, item, th);

  let maxX = W, maxY = H, minY = 0;
  if (dims) {
    const d1 = th * 2.6, d2 = columns.length > 1 ? th * 5 : d1;
    const v2 = rows.length > 1 ? th * 5 : d1;
    if (columns.length > 1) for (let c = 0; c < columns.length; c++) dimH(P, xs[c], xs[c + 1], H, H + d1, th);
    dimH(P, 0, W, H, H + d2, th);
    if (rows.length > 1) for (let r = 0; r < rows.length; r++) dimV(P, ys[r], ys[r + 1], W, W + d1, th);
    dimV(P, 0, H, W, W + v2, th);
    maxX = W + v2 + th; maxY = H + d2 + th;
  }
  if (labels) {
    pushText(P, 0, -th * 1.2, `${item.tag || ""}  ${item.width} × ${item.height}`, th * 1.2, "start", 0, "TITLE");
    minY = -th * 2.6;
  }
  const pad = th * 1.2;
  return { prims: P, bounds: { x: -pad, y: minY - pad, w: maxX + 2 * pad, h: maxY - minY + 2 * pad } };
}

export function newItem(tag = "W1", type = "window") {
  const door = type === "door";
  return {
    id: (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()),
    tag, type, name: door ? "Hinged door" : "Casement window", location: "", quantity: 1,
    width: door ? 1000 : 1500, height: door ? 2400 : 1200,
    columns: door ? [1000] : [750, 750], rows: door ? [2400] : [1200],
    cells: door ? [["door_left"]] : [["casement_left", "fixed"]], spans: [], panel_glass: {},
    frame_width: DEFAULT_FACE_WIDTH, mullion_width: DEFAULT_FACE_WIDTH, sash_width: DEFAULT_FACE_WIDTH, frame_depth: 60,
    typology: door ? "hinged_door_single" : "casement_window", source_typology: "",
    profile_system: door ? "YKK AP IWIN-S Casement Door" : "YKK AP IWIN-S Casement", glass_spec: "6mm + 12A + 6mm clear DGU", finish: "", hardware: "", notes: "",
  };
}

export function scaleParts(parts, total) {
  const s = parts.reduce((a, b) => a + b, 0) || 1;
  const out = parts.map((p) => Math.max(1, Math.round((p * total) / s)));
  out[out.length - 1] += total - out.reduce((a, b) => a + b, 0);
  return out;
}

export function setGridCount(item, axis, count) {
  const key = axis === "col" ? "columns" : "rows";
  const total = axis === "col" ? item.width : item.height;
  const n = Math.max(1, Math.min(8, count));
  const parts = scaleParts(Array(n).fill(1), total);
  const cells = axis === "col"
    ? item.rows.map((_, r) => parts.map((__, c) => item.cells?.[r]?.[c] || "fixed"))
    : parts.map((_, r) => item.columns.map((__, c) => item.cells?.[r]?.[c] || item.cells?.[item.cells.length - 1]?.[c] || "fixed"));
  const nr = cells.length, nc = cells[0].length;
  const spans = (item.spans || []).filter((s) => s.r + s.rs <= nr && s.c + s.cs <= nc);
  return { ...item, [key]: parts, cells, spans };
}

export const panelsOf = (item) => {
  const xs = cum(item.columns), ys = cum(item.rows);
  return visibleCells(item).map(({ r, c, rs, cs }) => ({
    x0: xs[c], x1: xs[c + cs], y0: ys[r], y1: ys[r + rs], op: item.cells[r][c], glass: item.panel_glass?.[`${r}-${c}`],
  }));
};

export function fromPanels(item, panels) {
  const uniq = (a) => [...new Set(a)].sort((p, q) => p - q);
  const xb = uniq(panels.flatMap((p) => [p.x0, p.x1])), yb = uniq(panels.flatMap((p) => [p.y0, p.y1]));
  const columns = xb.slice(1).map((v, i) => v - xb[i]), rows = yb.slice(1).map((v, i) => v - yb[i]);
  const cells = rows.map(() => columns.map(() => "fixed"));
  const spans = [], panel_glass = {};
  panels.forEach((p) => {
    const c = xb.indexOf(p.x0), cs = xb.indexOf(p.x1) - c, r = yb.indexOf(p.y0), rs = yb.indexOf(p.y1) - r;
    for (let i = r; i < r + rs; i++) for (let j = c; j < c + cs; j++) cells[i][j] = p.op;
    if (rs > 1 || cs > 1) spans.push({ r, c, rs, cs });
    if (p.glass) panel_glass[`${r}-${c}`] = p.glass;
  });
  return { ...item, columns, rows, cells, spans, panel_glass, width: xb[xb.length - 1], height: yb[yb.length - 1] };
}

export const compact = (item) => fromPanels(item, panelsOf(item));

export function divideCell(item, r, c, axis, n) {
  const xs = cum(item.columns), ys = cum(item.rows);
  const panels = panelsOf(item);
  const idx = panels.findIndex((p) => p.x0 === xs[c] && p.y0 === ys[r]);
  if (idx < 0 || n < 2) return item;
  const t = panels[idx];
  const len = axis === "col" ? t.x1 - t.x0 : t.y1 - t.y0;
  const cuts = Array.from({ length: n + 1 }, (_, i) => (i === n ? len : Math.round((len * i) / n)));
  const pieces = cuts.slice(0, -1).map((a, i) => (axis === "col"
    ? { ...t, x0: t.x0 + a, x1: t.x0 + cuts[i + 1] }
    : { ...t, y0: t.y0 + a, y1: t.y0 + cuts[i + 1] }));
  return fromPanels(item, [...panels.slice(0, idx), ...pieces, ...panels.slice(idx + 1)]);
}

export const cellAt = (item, x, y) => {
  const r = cum(item.rows).indexOf(y), c = cum(item.columns).indexOf(x);
  return r < 0 || c < 0 ? { r: 0, c: 0 } : anchorOf(item, r, c);
};
export const cellOrigin = (item, r, c) => ({ x: cum(item.columns)[c], y: cum(item.rows)[r] });
